import React, { useEffect, useState } from 'react'
import HeaderWithIcons from './HeaderWithIcons'
import Tree from 'react-d3-tree'
import { useDispatch, useSelector } from 'react-redux'
import { fetchOrgTreeData, selectOrgTree } from '../../slices/orgTreeApiSlice'
import {
  CircularProgress,
  Box,
  Avatar,
  Typography,
  Card,
  CardContent,
  Tooltip,
} from '@mui/material'
import avatarImg from '../../assets/avatar.png'

const transformToHierarchy = (flatData, maxDepth = 3) => {
  if (!flatData || flatData.length === 0) return null

  const idMap = {}
  const root = { id: 'root', name: 'Root', children: [] }

  flatData.forEach((item) => {
    idMap[item.id] = {
      id: item.id,
      name: item.name,
      designation: item.designation,
      bidid: item.bidid,
      code: item.code,
      isActive: item.isActive,
      children: [],
      isExpanded: false,
      totalChildren: 0,
      depth: 1,
    }
  })

  flatData.forEach((item) => {
    const currentItem = idMap[item.id]
    if (item.sponsoredBy === 0) {
      root.children.push(currentItem)
    } else {
      const parent = idMap[item.sponsoredBy]
      if (parent) {
        if (parent.depth < maxDepth) {
          currentItem.depth = parent.depth + 1
          parent.children.push(currentItem)
        } else {
          parent.totalChildren += 1
        }
      }
    }
  })

  return root.children.length ? root.children[0] : null
}

const renderCustomNodeElement = ({
  nodeDatum,
  handleToggleChildren,
  orientation,
  userId,
}) => {
  const isHorizontal = orientation === 'horizontal'
  const isCurrentUser = nodeDatum.bidid === `BID${userId}`

  return (
    <g>
      {/* Node Card */}
      <foreignObject x={-125} y={-50} width={240} height={85}>
        <Card
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'start',
            width: '100%',
            height: '100%',
            backgroundColor: isCurrentUser ? '#FFD0C3' : '#ffffff',
            boxShadow: 'none',
            borderRadius: '10px',
            padding: '8px',
            border: '1px solid #C6D3FE',
          }}>
          <Avatar
            src={avatarImg}
            alt={nodeDatum.name}
            sx={{ width: 55, height: 55, marginRight: '12px' }}
          />
          <Tooltip title={nodeDatum.name}>
            <CardContent sx={{ flex: '1 1 auto', padding: 0 }}>
              <Typography
                sx={{
                  fontFamily: 'Work Sans',
                  fontSize: '16px',
                  fontWeight: 500,
                  textAlign: 'left',
                  color: '#081332',
                  paddingTop: '25px',
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                  overflow: 'hidden',
                  display: '-webkit-box',
                  WebkitBoxOrient: 'vertical',
                  WebkitLineClamp: 2,
                  textOverflow: 'ellipsis',
                }}>
                {nodeDatum.name}
              </Typography>

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: '8px',
                }}>
                <Typography
                  variant='body2'
                  sx={{
                    fontFamily: 'Work Sans',
                    fontSize: '14px',
                    fontWeight: 700,
                    color: '#5E6066',
                  }}>
                  {nodeDatum.bidid}
                </Typography>
                <Typography
                  variant='caption'
                  sx={{
                    fontFamily: 'Work Sans',
                    fontSize: '18px',
                    fontWeight: 800,
                    color: '#F6764E',
                  }}>
                  {nodeDatum.code}
                </Typography>
              </Box>
            </CardContent>
          </Tooltip>
        </Card>
      </foreignObject>

      {/* Child Count Circle */}
      {nodeDatum.totalChildren > 0 && (
        <>
          <circle
            cx={isHorizontal ? '120' : '0'}
            cy={isHorizontal ? '0' : '40'}
            r='12'
            fill='#F6764E'
            onClick={() => handleToggleChildren(nodeDatum)}
            style={{ cursor: 'pointer', stroke: '#ddd', strokeWidth: 1 }}
          />
          <text
            x={isHorizontal ? '120' : '0'}
            y={isHorizontal ? '4' : '45'}
            textAnchor='middle'
            fontSize='12px'
            pointerEvents='none'
            fill='white'
            stroke='none'
            // strokeWidth='1'
            style={{ fontWeight: 'bold', borderColor: 'white', fill: 'white' }}>
            {nodeDatum.totalChildren}
          </text>
        </>
      )}
    </g>
  )
}

const OrganizationalTree = () => {
  const dispatch = useDispatch()
  const { data, loading, error } = useSelector(selectOrgTree)
  const user = useSelector((state) => state.auth.user)
  const userId = user?.match(/\d+/)?.[0]

  const [hierarchicalData, setHierarchicalData] = useState(null)
  const [translate, setTranslate] = useState({
    x: window.innerWidth / 2 - 160,
    y: 100,
  })
  const [zoom, setZoom] = useState(1)
  const [orientation, setOrientation] = useState('vertical')

  useEffect(() => {
    dispatch(fetchOrgTreeData())
  }, [dispatch])

  useEffect(() => {
    if (data?.length > 0) {
      const transformedData = transformToHierarchy(data, 3)
      setHierarchicalData(transformedData)
    }
  }, [data])

  const handleToggleChildren = (nodeDatum) => {
    const updatedData = deepClone(hierarchicalData)
    const nodeToUpdate = findNode(updatedData, nodeDatum.id)

    if (nodeToUpdate) {
      if (nodeToUpdate.isExpanded) {
        nodeToUpdate.children = []
        nodeToUpdate.isExpanded = false
      } else {
        const newChildren = data.filter(
          (item) => item.sponsoredBy === nodeDatum.id
        )
        nodeToUpdate.children = newChildren.map((child) => ({
          ...child,
          totalChildren: data.filter((item) => item.sponsoredBy === child.id)
            .length,
          isExpanded: false,
        }))
        nodeToUpdate.isExpanded = true
      }
    }

    setHierarchicalData(updatedData)
  }

  const handleSwapLayout = () => {
    const newOrientation =
      orientation === 'vertical' ? 'horizontal' : 'vertical'
    setOrientation(newOrientation)

    if (newOrientation === 'horizontal') {
      setTranslate({ x: 350, y: window.innerHeight / 2 - 100 })
    } else {
      setTranslate({ x: window.innerWidth / 2 - 160, y: 100 })
    }
  }

  const handleFitToScreen = () => {
    setTranslate({ x: 0, y: 0 })
    setZoom(1)

    setTimeout(() => {
      if (orientation === 'horizontal') {
        setTranslate({
          x: window.innerWidth / 2 - 340,
          y: window.innerHeight / 2 - 70,
        })
      } else {
        setTranslate({
          x: window.innerWidth / 2 - 160,
          y: 100,
        })
      }
    }, 0)
  }

  const deepClone = (obj) => JSON.parse(JSON.stringify(obj))

  const findNode = (node, id) => {
    if (node.id === id) return node
    if (node.children) {
      for (let child of node.children) {
        const found = findNode(child, id)
        if (found) return found
      }
    }
    return null
  }

  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: '100%',
          height: '100vh',
        }}>
        <CircularProgress color='primary' size={40} />
      </div>
    )
  }

  if (error) {
    return <div>Error: {error}</div>
  }

  return (
    <div style={{ width: '100%', height: '100vh' }}>
      <HeaderWithIcons
        onSwapLayout={handleSwapLayout}
        onFitToScreen={handleFitToScreen}
      />
      {hierarchicalData ? (
        <Tree
          data={hierarchicalData}
          orientation={orientation}
          pathFunc='step'
          nodeSize={{ x: 320, y: 160 }}
          translate={translate}
          zoom={zoom}
          renderCustomNodeElement={(rd3tProps) =>
            renderCustomNodeElement({
              ...rd3tProps,
              handleToggleChildren,
              orientation,
              userId,
            })
          }
        />
      ) : (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            height: '100%',
            fontSize: '18px',
            color: '#5E6066',
          }}>
          No data available to display.
        </div>
      )}
    </div>
  )
}

export default OrganizationalTree
